
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../config";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  updateDoc,
  getDocs,
  query,
  where,
  setDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { QRCodeCanvas } from "qrcode.react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Nav from "../Components/Nav";
import Footer from "../Components/Footer";

// Professional Icons
const Icon = ({ name, className = "", style = {} }) => {
  const icons = {
    plan: "fas fa-chart-line",
    wallet: "fas fa-wallet",
    credit: "fas fa-credit-card",
    check: "fas fa-check-circle",
    arrow: "fas fa-arrow-right",
    back: "fas fa-arrow-left",
    copy: "fas fa-copy",
    qr: "fas fa-qrcode",
    dollar: "fas fa-dollar-sign",
    clock: "fas fa-clock",
    gift: "fas fa-gift",
    trending: "fas fa-trending-up",
    shield: "fas fa-shield-alt",
    star: "fas fa-star",
    sparkle: "fas fa-sparkles",
  };
  return <i className={`${icons[name] || "fas fa-circle"} ${className}`} style={style} />;
};

function InvestmentFlow() {
  const [step, setStep] = useState("planSelection");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [amount, setAmount] = useState("");
  const [fundingSource, setFundingSource] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("");
  const [walletAddresses, setWalletAddresses] = useState({});
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [messages, setMessages] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const formatMax = (max) => {
    if (max === null || max === undefined || max === Infinity) return "Unlimited";
    return `$${Number(max).toLocaleString()}`;
  };

  const isValidAmount = (amount, plan) => {
    const numAmount = Number(amount);
    if (isNaN(numAmount) || numAmount < Number(plan.min)) return false;
    if (plan.max !== null && plan.max !== undefined && plan.max !== Infinity) {
      if (numAmount > Number(plan.max)) return false;
    }
    return true;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            setBalance(userDoc.data().balance || 0);
            setUsername(userDoc.data().fullname || currentUser.email);
          }

          const walletRef = doc(db, "settings", "walletAddresses");
          const walletSnapshot = await getDoc(walletRef);
          if (walletSnapshot.exists()) {
            setWalletAddresses(walletSnapshot.data());
          }

          const querySnapshot = await getDocs(collection(db, "investmentPlans"));

          const fetchedPlans = querySnapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              min: Number(data.min) || 0,
              max: data.max === null || data.max === undefined ? Infinity : Number(data.max),
              roi: Number(data.roi) || 0,
              duration: Number(data.duration) || 0,
            };
          });

          const sortedPlans = [...fetchedPlans].sort((a, b) => a.min - b.min);
          setPlans(sortedPlans);
        } catch (error) {
          console.error("Error fetching data:", error);
          setMessages({ global: "Error fetching user data or plans." });
        } finally {
          setLoading(false);
        }
      } else {
        setUser(null);
        navigate("/auth/login");
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (messages[step]) {
      const timer = setTimeout(() => {
        setMessages((prev) => ({ ...prev, [step]: "" }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [messages, step]);

  useEffect(() => {
    if (messages.confirmation) {
      if (
        messages.confirmation.includes("success") ||
        messages.confirmation.includes("copied")
      ) {
        toast.success(messages.confirmation, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error(messages.confirmation, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  }, [messages.confirmation]);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setAmount(plan.min.toString());
    setStep("funding");
  };

  const handleFundingSubmit = (e) => {
    e.preventDefault();
    const investmentAmount = parseFloat(amount);

    if (!fundingSource) {
      setMessages((prev) => ({ ...prev, funding: "Please select a funding source." }));
      return;
    }

    if (isNaN(investmentAmount) || investmentAmount <= 0) {
      setMessages((prev) => ({ ...prev, funding: "Please enter a valid amount." }));
      return;
    }

    if (investmentAmount < selectedPlan.min) {
      setMessages((prev) => ({
        ...prev,
        funding: `Amount must be at least $${selectedPlan.min.toLocaleString()}.`,
      }));
      return;
    }

    if (selectedPlan.max !== Infinity && investmentAmount > selectedPlan.max) {
      setMessages((prev) => ({
        ...prev,
        funding: `Amount cannot exceed ${formatMax(selectedPlan.max)}.`,
      }));
      return;
    }

    if (fundingSource === "paymentMethod" && !selectedMethod) {
      setMessages((prev) => ({ ...prev, funding: "Please select a payment method." }));
      return;
    }

    setStep("confirmation");
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    const investmentAmount = parseFloat(amount);

    try {
      if (fundingSource === "balance") {
        const investmentsRef = collection(db, "investments");
        const activeQuery = query(
          investmentsRef,
          where("userId", "==", user.uid),
          where("plan", "==", selectedPlan.name),
          where("status", "==", "active")
        );
        const activeSnapshot = await getDocs(activeQuery);
      }

      if (fundingSource === "balance" && investmentAmount > balance) {
        setMessages((prev) => ({ ...prev, confirmation: "Insufficient balance." }));
        setSubmitting(false);
        return;
      }

      if (fundingSource === "balance") {
        const investmentData = {
          userId: user.uid,
          email: user.email,
          username: username,
          plan: selectedPlan.name,
          roi: selectedPlan.roi,
          duration: selectedPlan.duration,
          amount: investmentAmount,
          createdAt: new Date(),
          status: "active",
        };

        await addDoc(collection(db, "investments"), investmentData);

        const userDocRef = doc(db, "users", user.uid);
        const newBalance = balance - investmentAmount;
        await updateDoc(userDocRef, { balance: newBalance });
        setBalance(newBalance);

        setMessages((prev) => ({
          ...prev,
          confirmation: "Investment activated successfully!",
        }));
      } else {
        const depositData = {
          userId: user.uid,
          userEmail: user.email,
          username: username,
          method: selectedMethod,
          amount: investmentAmount,
          timestamp: new Date().toISOString(),
          status: "pending",
          plan: selectedPlan.name,
          roi: selectedPlan.roi,
          duration: selectedPlan.duration,
        };

        const depositId = `${user.uid}_${Date.now()}`;
        await setDoc(doc(db, "deposits", depositId), depositData);

        await axios.post("https://renderbackend-4t2p.onrender.com/send-mail", {
          email: user.email,
          subject: "Your Investment Deposit Request",
          CompanyName: "APEXYEILD",
          htmlToSend: `
          <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
            <div style="text-align: center; margin-bottom: 20px;">
              <img src="https://apexyeild.com/public/uploads/logo/light.png" alt="APEXYEILD Logo" style="height: 60px;" />
              <h2 style="margin: 10px 0; color: #1a1a1a;">APEXYEILD</h2>
            </div>
            <h3 style="color: #333;">Hi! INVESTOR ${username}</h3>
            <p style="color: #555;">We've received your investment deposit request for the "${selectedPlan.name}" plan:</p>
            <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Plan:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedPlan.name}</td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedMethod}</td></tr>
              <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: #212529;">$${parseFloat(amount).toFixed(2)}</span></td></tr>
            </table>
            <p style="color: #555;">Please complete your deposit by sending the amount to the wallet address below:</p>
            <p style="color: #111;"><strong>${selectedMethod}:</strong> ${walletAddresses[selectedMethod] || "Not available"}</p>
            <p style="margin-top: 30px; color: #555;">Once we confirm the payment, your investment will be activated automatically.</p>
            <p style="margin-top: 20px; color: #555;">Thank you for choosing APEXYEILD!</p>
            <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
            <p style="font-size: 12px; color: #aaa; text-align: center;">© 2025 APEXYEILD. All rights reserved.</p>
          </div>
        `,
        });

        setMessages((prev) => ({
          ...prev,
          confirmation: "Deposit request submitted successfully. Please complete the payment to activate your investment.",
        }));
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Error processing investment:", error);
      setMessages((prev) => ({
        ...prev,
        confirmation: "Failed to process request: " + error.message,
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    const address = walletAddresses[selectedMethod];
    if (address) {
      navigator.clipboard
        .writeText(address)
        .then(() => {
          setMessages((prev) => ({ ...prev, confirmation: "Wallet address copied!" }));
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
          setMessages((prev) => ({ ...prev, confirmation: "Failed to copy wallet address." }));
        });
    } else {
      setMessages((prev) => ({ ...prev, confirmation: "No wallet address available." }));
    }
  };

  const handleBack = () => {
    if (step === "funding") {
      setSelectedPlan(null);
      setAmount("");
      setFundingSource("");
      setSelectedMethod("");
      setStep("planSelection");
    } else if (step === "confirmation") {
      setStep("funding");
    }
  };

  const handleRestart = () => {
    setStep("planSelection");
    setSelectedPlan(null);
    setAmount("");
    setFundingSource("");
    setSelectedMethod("");
    setMessages({});
    setIsModalOpen(false);
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>APEXYEILD | Invest</title>
        <link rel="icon" href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352" type="image/png" />
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN" crossOrigin="anonymous" />
        <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <style>{`
        :root {
          --primary: #0b57e3;
          --primary-dark: #0846b8;
          --success: #10b981;
          --warning: #f59e0b;
          --danger: #ef4444;
          --gold: #D4AF37;
          --gold-dark: #B89628;
        }
        
        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #f8fafc;
        }
        
        .ls-05 { letter-spacing: 0.05em; }
        
        /* Progress Steps */
        .progress-steps {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          position: relative;
        }
        
        .progress-steps::before {
          content: '';
          position: absolute;
          top: 24px;
          left: 0;
          right: 0;
          height: 2px;
          background: #e2e8f0;
          z-index: 0;
        }
        
        .step-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 1;
          flex: 1;
        }
        
        .step-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: #fff;
          border: 3px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1.1rem;
          color: #94a3b8;
          transition: all 0.3s ease;
          margin-bottom: 0.5rem;
        }
        
        .step-item.active .step-circle {
          background: var(--primary);
          border-color: var(--primary);
          color: #fff;
          box-shadow: 0 4px 12px rgba(11, 87, 227, 0.3);
        }
        
        .step-item.completed .step-circle {
          background: var(--success);
          border-color: var(--success);
          color: #fff;
        }
        
        .step-label {
          font-size: 0.85rem;
          font-weight: 500;
          color: #64748b;
          text-align: center;
        }
        
        .step-item.active .step-label {
          color: var(--primary);
          font-weight: 600;
        }
        
        .step-item.completed .step-label {
          color: var(--success);
        }
        
        /* Plan Cards */
        .plan-card {
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 2rem;
          transition: all 0.3s ease;
          background: #fff;
          position: relative;
          overflow: hidden;
          cursor: pointer;
        }
        
        .plan-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary), var(--primary-dark));
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        
        .plan-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0,0,0,0.12);
          border-color: var(--primary);
        }
        
        .plan-card:hover::before {
          transform: scaleX(1);
        }
        
        .plan-card.popular {
          border-color: var(--gold);
        }
        
        .plan-card.popular::before {
          background: linear-gradient(90deg, var(--gold), var(--gold-dark));
          transform: scaleX(1);
        }
        
        .popular-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: linear-gradient(135deg, var(--gold), var(--gold-dark));
          color: #fff;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        
        .plan-name {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 1.5rem;
        }
        
        .plan-feature {
          display: flex;
          align-items: center;
          padding: 0.75rem 0;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .plan-feature:last-child {
          border-bottom: none;
        }
        
        .plan-feature-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
          flex-shrink: 0;
        }
        
        .plan-feature-label {
          color: #64748b;
          font-size: 0.9rem;
          flex: 1;
        }
        
        .plan-feature-value {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
        }
        
        .plan-btn {
          width: 100%;
          padding: 0.875rem;
          border-radius: 10px;
          font-weight: 600;
          transition: all 0.2s ease;
          margin-top: 1.5rem;
        }
        
        .plan-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(11, 87, 227, 0.3);
        }
        
        /* Form Styling */
        .form-card {
          border: none;
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          background: #fff;
          padding: 2.5rem;
        }
        
        .form-label {
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.5rem;
          font-size: 0.95rem;
        }
        
        .form-control, .form-select {
          border-radius: 10px;
          border: 2px solid #e2e8f0;
          padding: 0.75rem 1rem;
          font-size: 0.95rem;
          transition: all 0.2s ease;
        }
        
        .form-control:focus, .form-select:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(11, 87, 227, 0.1);
          outline: none;
        }
        
        .input-group-text {
          border-radius: 10px 0 0 10px;
          border: 2px solid #e2e8f0;
          border-right: none;
          background: #f8fafc;
          font-weight: 600;
          color: #64748b;
        }
        
        .input-group .form-control {
          border-radius: 0 10px 10px 0;
          border-left: none;
        }
        
        /* Summary Card */
        .summary-card {
          background: linear-gradient(135deg, #f8fafc 0%, #fff 100%);
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 2rem;
        }
        
        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 0;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .summary-item:last-child {
          border-bottom: none;
          padding-top: 1.5rem;
          margin-top: 0.5rem;
          border-top: 2px solid #e2e8f0;
        }
        
        .summary-label {
          color: #64748b;
          font-size: 0.95rem;
          font-weight: 500;
        }
        
        .summary-value {
          color: #1e293b;
          font-weight: 700;
          font-size: 1.1rem;
        }
        
        .summary-item:last-child .summary-value {
          color: var(--primary);
          font-size: 1.5rem;
        }
        
        /* QR Code */
        .qr-container {
          background: #fff;
          border: 2px solid #e2e8f0;
          border-radius: 16px;
          padding: 2rem;
          text-align: center;
        }
        
        .qr-code {
          margin: 1.5rem auto;
          padding: 1rem;
          background: #fff;
          border-radius: 12px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          display: inline-block;
        }
        
        .wallet-address-box {
          background: #f8fafc;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          padding: 1rem;
          margin: 1rem 0;
          word-break: break-all;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          color: #1e293b;
        }
        
        .copy-btn {
          border-radius: 10px;
          padding: 0.625rem 1.5rem;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        
        .copy-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        /* Buttons */
        .btn-primary {
          background: var(--primary);
          border: none;
          border-radius: 10px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        
        .btn-primary:hover {
          background: var(--primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(11, 87, 227, 0.3);
        }
        
        .btn-outline-secondary {
          border-radius: 10px;
          padding: 0.75rem 2rem;
          font-weight: 600;
          border: 2px solid #e2e8f0;
          color: #64748b;
          transition: all 0.2s ease;
        }
        
        .btn-outline-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          color: #1e293b;
        }
        
        /* Modal */
        .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }
        
        .modal-header {
          border-bottom: 2px solid #f1f5f9;
          padding: 1.5rem 2rem;
        }
        
        .modal-body {
          padding: 2rem;
        }
        
        .modal-footer {
          border-top: 2px solid #f1f5f9;
          padding: 1.5rem 2rem;
        }
        
        /* Toast */
        .Toastify__toast {
          border-radius: 10px;
          font-weight: 500;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .plan-card {
            padding: 1.5rem;
          }
          
          .form-card {
            padding: 1.5rem;
          }
          
          .step-circle {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }
          
          .step-label {
            font-size: 0.75rem;
          }
        }
      `}</style>

      <div className="wrapper">
        <Nav />
        <div className="main-panel bg-light">
          <div className="content bg-light">
            <div className="page-inner py-4">
              
              {/* Header */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom">
                <div>
                  <h2 className="fw-bold text-dark mb-1">Start Your Investment</h2>
                  <p className="text-muted mb-0">Choose a plan and grow your portfolio</p>
                </div>
                <div className="mt-3 mt-md-0">
                  <div className="d-flex align-items-center gap-2 px-4 py-2 bg-white rounded-3 shadow-sm">
                    <Icon name="wallet" className="text-primary" />
                    <span className="text-muted small">Balance:</span>
                    <span className="fw-bold text-dark">${balance.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

              {/* Progress Steps */}
              <div className="progress-steps">
                <div className={`step-item ${step === "planSelection" ? "active" : step === "funding" || step === "confirmation" ? "completed" : ""}`}>
                  <div className="step-circle">
                    {step === "planSelection" ? "1" : <Icon name="check" />}
                  </div>
                  <div className="step-label">Select Plan</div>
                </div>
                <div className={`step-item ${step === "funding" ? "active" : step === "confirmation" ? "completed" : ""}`}>
                  <div className="step-circle">
                    {step === "funding" ? "2" : step === "confirmation" ? <Icon name="check" /> : "2"}
                  </div>
                  <div className="step-label">Funding</div>
                </div>
                <div className={`step-item ${step === "confirmation" ? "active" : ""}`}>
                  <div className="step-circle">3</div>
                  <div className="step-label">Confirm</div>
                </div>
              </div>

              {/* Step 1: Plan Selection */}
              {step === "planSelection" && (
                <div>
                  <div className="mb-4">
                    <h4 className="fw-bold text-dark mb-1">Choose Your Investment Plan</h4>
                    <p className="text-muted">Select a plan that matches your investment goals</p>
                  </div>
                  
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-3 text-muted">Loading investment plans...</p>
                    </div>
                  ) : plans.length === 0 ? (
                    <div className="text-center py-5">
                      <Icon name="plan" className="fa-3x text-muted opacity-50 mb-3" />
                      <p className="text-muted">No investment plans available at the moment.</p>
                    </div>
                  ) : (
                    <div className="row g-4">
                      {plans.map((plan, index) => (
                        <div className="col-lg-4 col-md-6" key={plan.id}>
                          <div className={`plan-card h-100 ${index === 1 ? "popular" : ""}`}>
                            {index === 1 && <div className="popular-badge">Most Popular</div>}
                            
                            <h3 className="plan-name">{plan.name}</h3>
                            
                            <div className="plan-features">
                              <div className="plan-feature">
                                <div className="plan-feature-icon" style={{ background: "rgba(11, 87, 227, 0.1)", color: "var(--primary)" }}>
                                  <Icon name="dollar" />
                                </div>
                                <div className="plan-feature-label">Minimum</div>
                                <div className="plan-feature-value">${plan.min.toLocaleString()}</div>
                              </div>
                              
                              <div className="plan-feature">
                                <div className="plan-feature-icon" style={{ background: "rgba(16, 185, 129, 0.1)", color: "var(--success)" }}>
                                  <Icon name="trending" />
                                </div>
                                <div className="plan-feature-label">Maximum</div>
                                <div className="plan-feature-value">{formatMax(plan.max)}</div>
                              </div>
                              
                              <div className="plan-feature">
                                <div className="plan-feature-icon" style={{ background: "rgba(245, 158, 11, 0.1)", color: "var(--warning)" }}>
                                  <Icon name="star" />
                                </div>
                                <div className="plan-feature-label">ROI</div>
                                <div className="plan-feature-value">{plan.roi}%</div>
                              </div>
                              
                              <div className="plan-feature">
                                <div className="plan-feature-icon" style={{ background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>
                                  <Icon name="gift" />
                                </div>
                                <div className="plan-feature-label">Referral Bonus</div>
                                <div className="plan-feature-value">{plan.referralBonus || 5}%</div>
                              </div>
                              
                              <div className="plan-feature">
                                <div className="plan-feature-icon" style={{ background: "rgba(236, 72, 153, 0.1)", color: "#ec4899" }}>
                                  <Icon name="clock" />
                                </div>
                                <div className="plan-feature-label">Duration</div>
                                <div className="plan-feature-value">{plan.duration} days</div>
                              </div>
                            </div>
                            
                            <button className="btn btn-primary plan-btn" onClick={() => handleSelectPlan(plan)}>
                              Choose Plan <Icon name="arrow" className="ms-2" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Funding */}
              {step === "funding" && selectedPlan && (
                <div>
                  <div className="mb-4">
                    <h4 className="fw-bold text-dark mb-1">Funding Details for {selectedPlan.name}</h4>
                    <p className="text-muted">Enter your investment amount and choose how to fund it</p>
                  </div>
                  
                  {messages.funding && (
                    <div className={`alert ${messages.funding.includes("success") ? "alert-success" : "alert-danger"} mb-4`} role="alert">
                      {messages.funding}
                    </div>
                  )}
                  
                  <div className="row g-4">
                    <div className="col-lg-7">
                      <div className="form-card">
                        <form onSubmit={handleFundingSubmit}>
                          <div className="mb-4">
                            <label className="form-label">Investment Amount</label>
                            <div className="input-group">
                              <span className="input-group-text">$</span>
                              <input
                                type="number"
                                className="form-control"
                                placeholder={`Min: $${selectedPlan.min.toLocaleString()}`}
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                required
                                min={selectedPlan.min}
                                max={selectedPlan.max === Infinity ? undefined : selectedPlan.max}
                                step="0.01"
                                aria-label="Investment amount"
                              />
                            </div>
                            <small className="text-muted mt-2 d-block">
                              Range: ${selectedPlan.min.toLocaleString()} - {formatMax(selectedPlan.max)}
                            </small>
                          </div>
                          
                          <div className="mb-4">
                            <label className="form-label">Funding Source</label>
                            <select 
                              className="form-select" 
                              value={fundingSource} 
                              onChange={(e) => setFundingSource(e.target.value)} 
                              required 
                              aria-label="Funding source"
                            >
                              <option value="" disabled>Select funding source</option>
                              <option value="balance">💰 Invest from Balance (${balance.toFixed(2)})</option>
                              <option value="paymentMethod">💳 Deposit with Payment Method</option>
                            </select>
                          </div>
                          
                          {fundingSource === "paymentMethod" && (
                            <div className="mb-4">
                              <label className="form-label">Payment Method</label>
                              <select 
                                className="form-select" 
                                value={selectedMethod} 
                                onChange={(e) => setSelectedMethod(e.target.value)} 
                                required 
                                aria-label="Payment method"
                              >
                                <option value="" disabled>Select a payment method</option>
                                {Object.keys(walletAddresses).map((method) => (
                                  <option key={method} value={method}>{method}</option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          <div className="d-flex gap-3 mt-4">
                            <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleBack}>
                              <Icon name="back" className="me-2" />Back
                            </button>
                            <button type="submit" className="btn btn-primary flex-fill" disabled={submitting}>
                              Next <Icon name="arrow" className="ms-2" />
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                    
                    <div className="col-lg-5">
                      <div className="summary-card">
                        <h5 className="fw-bold text-dark mb-3">Investment Summary</h5>
                        <div className="summary-item">
                          <span className="summary-label">Plan</span>
                          <span className="summary-value">{selectedPlan.name}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">ROI</span>
                          <span className="summary-value">{selectedPlan.roi}%</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Duration</span>
                          <span className="summary-value">{selectedPlan.duration} days</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Amount</span>
                          <span className="summary-value">${parseFloat(amount || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Confirmation */}
              {step === "confirmation" && selectedPlan && (
                <div>
                  <div className="mb-4">
                    <h4 className="fw-bold text-dark mb-1">Confirm Your Investment</h4>
                    <p className="text-muted">Review your details and confirm to proceed</p>
                  </div>
                  
                  {messages.confirmation && (
                    <div className={`alert ${messages.confirmation.includes("success") || messages.confirmation.includes("copied") ? "alert-success" : "alert-danger"} mb-4`} role="alert">
                      {messages.confirmation}
                    </div>
                  )}
                  
                  <div className="row g-4">
                    <div className="col-lg-7">
                      <div className="summary-card">
                        <h5 className="fw-bold text-dark mb-3">Investment Details</h5>
                        <div className="summary-item">
                          <span className="summary-label">Plan</span>
                          <span className="summary-value">{selectedPlan.name}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">ROI</span>
                          <span className="summary-value">{selectedPlan.roi}%</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Duration</span>
                          <span className="summary-value">{selectedPlan.duration} days</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Funding Source</span>
                          <span className="summary-value">{fundingSource === "balance" ? "Balance" : selectedMethod}</span>
                        </div>
                        <div className="summary-item">
                          <span className="summary-label">Total Amount</span>
                          <span className="summary-value">${parseFloat(amount).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      <div className="d-flex gap-3 mt-4">
                        <button type="button" className="btn btn-outline-secondary flex-fill" onClick={handleBack}>
                          <Icon name="back" className="me-2" />Back
                        </button>
                        <button 
                          type="button" 
                          className="btn btn-primary flex-fill" 
                          onClick={handleConfirm} 
                          disabled={submitting}
                        >
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                              Processing...
                            </>
                          ) : (
                            <>
                              <Icon name="check" className="me-2" />Confirm Investment
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {fundingSource === "paymentMethod" && (
                      <div className="col-lg-5">
                        <div className="qr-container">
                          <h5 className="fw-bold text-dark mb-3">
                            <Icon name="qr" className="me-2" />{selectedMethod} Wallet
                          </h5>
                          <div className="qr-code">
                            <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={180} />
                          </div>
                          <div className="wallet-address-box">
                            {walletAddresses[selectedMethod] || "Not available"}
                          </div>
                          <button 
                            type="button" 
                            className="btn btn-outline-primary copy-btn w-100" 
                            onClick={copyToClipboard}
                          >
                            <Icon name="copy" className="me-2" />Copy Address
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Restart Button */}
              {step === "confirmation" && messages.confirmation && (messages.confirmation.includes("success") || messages.confirmation.includes("submitted")) && (
                <div className="text-center mt-4">
                  <button className="btn btn-outline-primary btn-lg px-5" onClick={handleRestart}>
                    <Icon name="sparkle" className="me-2" />Start New Investment
                  </button>
                </div>
              )}

              {/* Deposit Confirmation Modal */}
              {isModalOpen && (
                <>
                  <div className="modal fade show d-block" id="depositModal" tabIndex="-1" aria-labelledby="depositModalLabel" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title fw-bold" id="depositModalLabel">
                            <Icon name="check" className="text-success me-2" />Deposit Request Submitted
                          </h5>
                          <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                          <p className="mb-3">
                            Your investment deposit request for <strong className="text-primary">${parseFloat(amount).toFixed(2)}</strong> via <strong>{selectedMethod}</strong> for the <strong>{selectedPlan.name}</strong> plan has been submitted.
                          </p>
                          <p className="mb-3">Please send the amount to the following wallet address:</p>
                          
                          <div className="qr-container">
                            <div className="qr-code">
                              <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={160} />
                            </div>
                            <div className="wallet-address-box">
                              {walletAddresses[selectedMethod] || "Not available"}
                            </div>
                            <button 
                              type="button" 
                              className="btn btn-outline-primary copy-btn w-100" 
                              onClick={copyToClipboard}
                            >
                              <Icon name="copy" className="me-2" />Copy Address
                            </button>
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button type="button" className="btn btn-primary px-4" onClick={() => setIsModalOpen(false)}>
                            Close
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestmentFlow;

// import React, { useState, useEffect } from "react";
// import { Helmet } from "react-helmet";
// import { useNavigate } from "react-router-dom";
// import { db, auth } from "../config";
// import {
//   doc,
//   getDoc,
//   addDoc,
//   collection,
//   updateDoc,
//   getDocs,
//   query,
//   where,
//   setDoc,
// } from "firebase/firestore";
// import { onAuthStateChanged } from "firebase/auth";
// import { QRCodeCanvas } from "qrcode.react";
// import axios from "axios";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import Nav from "../Components/Nav";
// import Footer from "../Components/Footer";

// function InvestmentFlow() {
//   const [step, setStep] = useState("planSelection");
//   const [plans, setPlans] = useState([]);
//   const [selectedPlan, setSelectedPlan] = useState(null);
//   const [amount, setAmount] = useState("");
//   const [fundingSource, setFundingSource] = useState("");
//   const [selectedMethod, setSelectedMethod] = useState("");
//   const [walletAddresses, setWalletAddresses] = useState({});
//   const [user, setUser] = useState(null);
//   const [username, setUsername] = useState(null);
//   const [balance, setBalance] = useState(0);
//   const [loading, setLoading] = useState(true);
//   const [submitting, setSubmitting] = useState(false);
//   const [messages, setMessages] = useState({});
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const navigate = useNavigate();

//   // 🎨 Helper: Format max value for display (null/Infinity → "Unlimited")
//   const formatMax = (max) => {
//     if (max === null || max === undefined || max === Infinity) return "Unlimited";
//     return `$${Number(max).toLocaleString()}`;
//   };

//   // 🎨 Helper: Check if amount is within plan limits
//   const isValidAmount = (amount, plan) => {
//     const numAmount = Number(amount);
//     if (isNaN(numAmount) || numAmount < Number(plan.min)) return false;
//     // Skip max check if plan.max is null/Infinity (unlimited)
//     if (plan.max !== null && plan.max !== undefined && plan.max !== Infinity) {
//       if (numAmount > Number(plan.max)) return false;
//     }
//     return true;
//   };

//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
//       if (currentUser) {
//         setUser(currentUser);
//         try {
//           const userDocRef = doc(db, "users", currentUser.uid);
//           const userDoc = await getDoc(userDocRef);
//           if (userDoc.exists()) {
//             setBalance(userDoc.data().balance || 0);
//             setUsername(userDoc.data().fullname || currentUser.email);
//           }

//           const walletRef = doc(db, "settings", "walletAddresses");
//           const walletSnapshot = await getDoc(walletRef);
//           if (walletSnapshot.exists()) {
//             setWalletAddresses(walletSnapshot.data());
//           }

//           const querySnapshot = await getDocs(collection(db, "investmentPlans"));

//           const fetchedPlans = querySnapshot.docs.map((doc) => {
//             const data = doc.data();
//             return {
//               id: doc.id,
//               ...data,
//               // 🔁 Convert Firestore null → JS Infinity for sorting/validation
//               min: Number(data.min) || 0,
//               max: data.max === null || data.max === undefined ? Infinity : Number(data.max),
//               roi: Number(data.roi) || 0,
//               duration: Number(data.duration) || 0,
//             };
//           });

//           // 🔽 Sort by minimum deposit (ascending)
//           const sortedPlans = [...fetchedPlans].sort((a, b) => a.min - b.min);

//           setPlans(sortedPlans);
//         } catch (error) {
//           console.error("Error fetching data:", error);
//           setMessages({ global: "Error fetching user data or plans." });
//         } finally {
//           setLoading(false);
//         }
//       } else {
//         setUser(null);
//         navigate("/auth/login");
//       }
//     });

//     return () => unsubscribe();
//   }, [navigate]);

//   useEffect(() => {
//     if (messages[step]) {
//       const timer = setTimeout(() => {
//         setMessages((prev) => ({ ...prev, [step]: "" }));
//       }, 3000);
//       return () => clearTimeout(timer);
//     }
//   }, [messages, step]);

//   useEffect(() => {
//     if (messages.confirmation) {
//       if (
//         messages.confirmation.includes("success") ||
//         messages.confirmation.includes("copied")
//       ) {
//         toast.success(messages.confirmation, {
//           position: "top-right",
//           autoClose: 3000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//         });
//       } else {
//         toast.error(messages.confirmation, {
//           position: "top-right",
//           autoClose: 3000,
//           hideProgressBar: false,
//           closeOnClick: true,
//           pauseOnHover: true,
//           draggable: true,
//         });
//       }
//     }
//   }, [messages.confirmation]);

//   const handleSelectPlan = (plan) => {
//     setSelectedPlan(plan);
//     setAmount(plan.min.toString());
//     setStep("funding");
//   };

//   const handleFundingSubmit = (e) => {
//     e.preventDefault();
//     const investmentAmount = parseFloat(amount);

//     if (!fundingSource) {
//       setMessages((prev) => ({ ...prev, funding: "Please select a funding source." }));
//       return;
//     }

//     if (isNaN(investmentAmount) || investmentAmount <= 0) {
//       setMessages((prev) => ({ ...prev, funding: "Please enter a valid amount." }));
//       return;
//     }

//     if (investmentAmount < selectedPlan.min) {
//       setMessages((prev) => ({
//         ...prev,
//         funding: `Amount must be at least $${selectedPlan.min.toLocaleString()}.`,
//       }));
//       return;
//     }

//     // ✅ Validation: Skip max check if plan.max is Infinity (unlimited)
//     if (selectedPlan.max !== Infinity && investmentAmount > selectedPlan.max) {
//       setMessages((prev) => ({
//         ...prev,
//         funding: `Amount cannot exceed ${formatMax(selectedPlan.max)}.`,
//       }));
//       return;
//     }

//     if (fundingSource === "paymentMethod" && !selectedMethod) {
//       setMessages((prev) => ({ ...prev, funding: "Please select a payment method." }));
//       return;
//     }

//     setStep("confirmation");
//   };

//   const handleConfirm = async () => {
//     setSubmitting(true);
//     const investmentAmount = parseFloat(amount);

//     try {
//       if (fundingSource === "balance") {
//         const investmentsRef = collection(db, "investments");
//         const activeQuery = query(
//           investmentsRef,
//           where("userId", "==", user.uid),
//           where("plan", "==", selectedPlan.name),
//           where("status", "==", "active")
//         );
//         const activeSnapshot = await getDocs(activeQuery);
//       }

//       if (fundingSource === "balance" && investmentAmount > balance) {
//         setMessages((prev) => ({ ...prev, confirmation: "Insufficient balance." }));
//         setSubmitting(false);
//         return;
//       }

//       if (fundingSource === "balance") {
//         const investmentData = {
//           userId: user.uid,
//           email: user.email,
//           username: username,
//           plan: selectedPlan.name,
//           roi: selectedPlan.roi,
//           duration: selectedPlan.duration,
//           amount: investmentAmount,
//           createdAt: new Date(),
//           status: "active",
//         };

//         await addDoc(collection(db, "investments"), investmentData);

//         const userDocRef = doc(db, "users", user.uid);
//         const newBalance = balance - investmentAmount;
//         await updateDoc(userDocRef, { balance: newBalance });
//         setBalance(newBalance);

//         setMessages((prev) => ({
//           ...prev,
//           confirmation: "Investment activated successfully!",
//         }));
//       } else {
//         const depositData = {
//           userId: user.uid,
//           userEmail: user.email,
//           username: username,
//           method: selectedMethod,
//           amount: investmentAmount,
//           timestamp: new Date().toISOString(),
//           status: "pending",
//           plan: selectedPlan.name,
//           roi: selectedPlan.roi,
//           duration: selectedPlan.duration,
//         };

//         const depositId = `${user.uid}_${Date.now()}`;
//         await setDoc(doc(db, "deposits", depositId), depositData);

//         await axios.post("https://renderbackend-4t2p.onrender.com/send-mail", {
//           email: user.email,
//           subject: "Your Investment Deposit Request",
//           CompanyName: "APEXYEILD",
//           htmlToSend: `
//           <div style="font-family: Arial, sans-serif; max-width: 600px; margin: auto; border: 1px solid #eee; padding: 20px; border-radius: 10px; background-color: #f9f9f9;">
//             <div style="text-align: center; margin-bottom: 20px;">
//               <img src="https://apexyeild.com/public/uploads/logo/light.png" alt="APEXYEILD Logo" style="height: 60px;" />
//               <h2 style="margin: 10px 0; color: #1a1a1a;">APEXYEILD</h2>
//             </div>
//             <h3 style="color: #333;">Hi! INVESTOR ${username}</h3>
//             <p style="color: #555;">We've received your investment deposit request for the "${selectedPlan.name}" plan:</p>
//             <table style="width: 100%; border-collapse: collapse; margin: 15px 0;">
//               <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Plan:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedPlan.name}</td></tr>
//               <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Method:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;">${selectedMethod}</td></tr>
//               <tr><td style="padding: 8px; border-bottom: 1px solid #ddd;"><strong>Amount:</strong></td><td style="padding: 8px; border-bottom: 1px solid #ddd;"><span style="color: #212529;">$${parseFloat(amount).toFixed(2)}</span></td></tr>
//             </table>
//             <p style="color: #555;">Please complete your deposit by sending the amount to the wallet address below:</p>
//             <p style="color: #111;"><strong>${selectedMethod}:</strong> ${walletAddresses[selectedMethod] || "Not available"}</p>
//             <p style="margin-top: 30px; color: #555;">Once we confirm the payment, your investment will be activated automatically.</p>
//             <p style="margin-top: 20px; color: #555;">Thank you for choosing APEXYEILD!</p>
//             <hr style="margin: 30px 0; border: none; border-top: 1px solid #ddd;" />
//             <p style="font-size: 12px; color: #aaa; text-align: center;">© 2025 APEXYEILD. All rights reserved.</p>
//           </div>
//         `,
//         });

//         setMessages((prev) => ({
//           ...prev,
//           confirmation: "Deposit request submitted successfully. Please complete the payment to activate your investment.",
//         }));
//         setIsModalOpen(true);
//       }
//     } catch (error) {
//       console.error("Error processing investment:", error);
//       setMessages((prev) => ({
//         ...prev,
//         confirmation: "Failed to process request: " + error.message,
//       }));
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   const copyToClipboard = () => {
//     const address = walletAddresses[selectedMethod];
//     if (address) {
//       navigator.clipboard
//         .writeText(address)
//         .then(() => {
//           setMessages((prev) => ({ ...prev, confirmation: "Wallet address copied!" }));
//         })
//         .catch((err) => {
//           console.error("Failed to copy: ", err);
//           setMessages((prev) => ({ ...prev, confirmation: "Failed to copy wallet address." }));
//         });
//     } else {
//       setMessages((prev) => ({ ...prev, confirmation: "No wallet address available." }));
//     }
//   };

//   const handleBack = () => {
//     if (step === "funding") {
//       setSelectedPlan(null);
//       setAmount("");
//       setFundingSource("");
//       setSelectedMethod("");
//       setStep("planSelection");
//     } else if (step === "confirmation") {
//       setStep("funding");
//     }
//   };

//   const handleRestart = () => {
//     setStep("planSelection");
//     setSelectedPlan(null);
//     setAmount("");
//     setFundingSource("");
//     setSelectedMethod("");
//     setMessages({});
//     setIsModalOpen(false);
//   };

//   return (
//     <div>
//       <Helmet>
//         <meta charSet="utf-8" />
//         <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
//         <meta name="viewport" content="width=device-width, initial-scale=1" />
//         <title>APEXYEILD | Invest</title>
//         <link rel="icon" href="https://apexyeild.com/auth/storage/app/public/photos/IQAXeGtikmark.png1744508352" type="image/png" />
//         <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css" integrity="sha384-T3c6CoIi6uLrA9TneNEoa7RxnatzjcDSCmG1MXxSR1GAsXEV/Dwwykc2MPK8M2HN" crossOrigin="anonymous" />
//       </Helmet>

//       <style>{`
//         .custom-select {
//           display: inline-block;
//           max-width: 100%;
//           height: calc(2.25rem + 2px);
//           padding: .375rem 1.75rem .375rem .75rem;
//           line-height: 1.5;
//           color: #0b57e3;
//           vertical-align: middle;
//           background: #fff url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='%23333' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E") no-repeat right .75rem center;
//           background-size: 8px 10px;
//           border: 4px solid black;
//           border-radius: .25rem;
//           -webkit-appearance: none;
//           -moz-appearance: none;
//           appearance: none;
//         }
//         .custom-select:focus { border-color: #80bdff; outline: 0; }
//         .custom-select:disabled { color: #868e96; background-color: #e9ecef; }
//         .pricing-table { transition: all 0.3s ease; }
//         .pricing-table:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15) !important; }
//         .pricing-features .feature { margin-bottom: 10px; }
//         .pricing-features .feature span { color: #212529; font-weight: bold; }
//         .message { margin-top: 15px; padding: 10px; border-radius: 5px; }
//         .message.success { background-color: #d4edda; color: #155724; }
//         .message.error { background-color: #f8d7da; color: #721c24; }
//         .Toastify__toast--success { background-color: #d4edda; color: #155724; }
//         .Toastify__toast--error { background-color: #f8d7da; color: #721c24; }
//         .qr-code-container { text-align: center; margin: 15px 0; }
//         .wallet-address { word-break: break-all; margin-top: 10px; color: #111; }
//         .footer-block { min-height: 400px; padding: 50px 0; background: linear-gradient(to right, #212529, #131516), url(../img/project4.jpg); }
//         .foot-header { font-family: "Montserrat", Sans-serif; font-weight: 600; font-size: 1rem; margin-bottom: 25px; color: #4A4A4A; }
//         .foot { display: block; font-size: small; margin-bottom: 8px; font-family: "Montserrat", Sans-serif; color: #A1A39E !important; }
//         .foot:hover { text-decoration: none !important; color: #FE413C !important; }
//         .foot::before { content: "-"; margin-right: 3px; color: #FE413C; }
//         .footer-credit { font-family: "Montserrat", Sans-serif; color: #FE413C; font-size: small; }
//         .footer-block .small-image { width: 250px; height: auto; }
//       `}</style>

//       <div className="wrapper">
//         <Nav />
//         <div className="main-panel bg-light">
//           <div className="content bg-light">
//             <div className="page-inner">
//               <div className="mt-2 mb-4">
//                 <h1 className="title1 text-dark">Start Your Investment</h1>
//                 <p className="text-dark">
//                   Available Balance: <span style={{ color: "#212529" }}>${balance.toFixed(2)}</span>
//                 </p>
//                 {messages.global && (
//                   <div className={`message ${messages.global.includes("success") ? "success" : "error"}`}>
//                     {messages.global}
//                   </div>
//                 )}
//               </div>

//               <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

//               <div className="mb-4">
//                 <div className="progress" style={{ height: "30px" }}>
//                   <div className="progress-bar bg-primary" role="progressbar" style={{ width: `${step === "planSelection" ? 33 : step === "funding" ? 66 : 100}%` }} aria-valuenow={step === "planSelection" ? 33 : step === "funding" ? 66 : 100} aria-valuemin="0" aria-valuemax="100">
//                     {step === "planSelection" ? "Step 1: Select Plan" : step === "funding" ? "Step 2: Choose Funding" : "Step 3: Confirm"}
//                   </div>
//                 </div>
//               </div>

//               {/* Step 1: Plan Selection */}
//               {step === "planSelection" && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Select an Investment Plan</h3>
//                   {loading ? (
//                     <div className="text-center"><i className="fas fa-spinner fa-spin fa-2x"></i> Loading...</div>
//                   ) : plans.length === 0 ? (
//                     <p className="text-dark">No investment plans available.</p>
//                   ) : (
//                     <div className="row">
//                       {plans.map((plan) => (
//                         <div className="col-lg-4 mb-4" key={plan.id}>
//                           <div className="pricing-table card border bg-light shadow p-4">
//                             <h2 className="text-dark text-center">{plan.name}</h2>
//                             <div className="pricing-features">
//                               <div className="feature text-dark">Minimum: <span>${plan.min.toLocaleString()}</span></div>
//                               <div className="feature text-dark">Maximum: <span>{formatMax(plan.max)}</span></div>
//                               <div className="feature text-dark">Cumulative Profit: <span>{plan.roi}%</span></div>
//                               <div className="feature text-dark">Referral Bonus: <span>{plan.referralBonus || 5}%</span></div>
//                               <div className="feature text-dark">Duration: <span>{plan.duration} days</span></div>
//                             </div>
//                             <button className="btn btn-block btn-primary mt-3" onClick={() => handleSelectPlan(plan)}>Choose Plan</button>
//                           </div>
//                         </div>
//                       ))}
//                     </div>
//                   )}
//                 </div>
//               )}

//               {/* Step 2: Funding */}
//               {step === "funding" && selectedPlan && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Choose Funding Source for {selectedPlan.name}</h3>
//                   {messages.funding && (
//                     <div className={`message ${messages.funding.includes("success") ? "success" : "error"}`}>{messages.funding}</div>
//                   )}
//                   <form onSubmit={handleFundingSubmit} className="card bg-light shadow p-4">
//                     <div className="mb-3">
//                       <h5 className="card-title text-dark">Enter Investment Amount</h5>
//                       <input
//                         type="number"
//                         className="form-control text-dark bg-light"
//                         placeholder={`Minimum: $${selectedPlan.min.toLocaleString()}`}
//                         value={amount}
//                         onChange={(e) => setAmount(e.target.value)}
//                         required
//                         min={selectedPlan.min}
//                         // ✅ Only set max attribute if plan has a limit
//                         max={selectedPlan.max === Infinity ? undefined : selectedPlan.max}
//                         step="0.01"
//                         aria-label="Investment amount"
//                       />
//                     </div>
//                     <div className="mb-3">
//                       <h5 className="card-title text-dark">Select Funding Source</h5>
//                       <select className="custom-select" value={fundingSource} onChange={(e) => setFundingSource(e.target.value)} required aria-label="Funding source">
//                         <option value="" disabled>Select funding source</option>
//                         <option value="balance">Invest from Balance</option>
//                         <option value="paymentMethod">Deposit with Payment Method</option>
//                       </select>
//                     </div>
//                     {fundingSource === "paymentMethod" && (
//                       <div className="mb-3">
//                         <h5 className="card-title text-dark">Choose Payment Method</h5>
//                         <select className="custom-select" value={selectedMethod} onChange={(e) => setSelectedMethod(e.target.value)} required aria-label="Payment method">
//                           <option value="" disabled>Select a payment method</option>
//                           {Object.keys(walletAddresses).map((method) => (
//                             <option key={method} value={method}>{method}</option>
//                           ))}
//                         </select>
//                       </div>
//                     )}
//                     <div className="d-flex justify-content-between">
//                       <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>Back</button>
//                       <button type="submit" className="btn btn-primary" disabled={submitting}>Next</button>
//                     </div>
//                   </form>
//                 </div>
//               )}

//               {/* Step 3: Confirmation */}
//               {step === "confirmation" && selectedPlan && (
//                 <div className="step-transition">
//                   <h3 className="text-dark mb-3">Confirm Your Investment</h3>
//                   {messages.confirmation && (
//                     <div className={`message ${messages.confirmation.includes("success") || messages.confirmation.includes("copied") ? "success" : "error"}`}>{messages.confirmation}</div>
//                   )}
//                   <div className="card bg-light shadow p-4">
//                     <h5 className="card-title text-dark">Investment Details</h5>
//                     <div className="pricing-features">
//                       <div className="feature text-dark">Plan: <span>{selectedPlan.name}</span></div>
//                       <div className="feature text-dark">Amount: <span style={{ color: "#212529" }}>${parseFloat(amount).toFixed(2)}</span></div>
//                       <div className="feature text-dark">Funding Source: <span>{fundingSource === "balance" ? "Balance" : selectedMethod}</span></div>
//                     </div>
//                     {fundingSource === "paymentMethod" && (
//                       <div className="qr-code-container">
//                         <h5 className="card-title text-dark">{selectedMethod} Wallet Address</h5>
//                         <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={128} />
//                         <p className="wallet-address">{walletAddresses[selectedMethod] || "Not available"}</p>
//                         <button type="button" className="btn btn-outline-secondary mt-2" onClick={copyToClipboard}>Copy Address</button>
//                       </div>
//                     )}
//                     <div className="d-flex justify-content-between mt-3">
//                       <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>Back</button>
//                       <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={submitting}>
//                         {submitting ? "Processing..." : "Confirm Investment"}
//                       </button>
//                     </div>
//                   </div>
//                 </div>
//               )}

//               {/* Restart Button */}
//               {step === "confirmation" && messages.confirmation && (messages.confirmation.includes("success") || messages.confirmation.includes("submitted")) && (
//                 <div className="text-center mt-3">
//                   <button className="btn btn-outline-primary" onClick={handleRestart}>Start New Investment</button>
//                 </div>
//               )}

//               {/* Deposit Confirmation Modal */}
//               {isModalOpen && (
//                 <>
//                   <div className="modal fade show d-block" id="depositModal" tabIndex="-1" aria-labelledby="depositModalLabel">
//                     <div className="modal-dialog">
//                       <div className="modal-content">
//                         <div className="modal-header">
//                           <h5 className="modal-title" id="depositModalLabel">Investment Deposit Request Submitted</h5>
//                           <button type="button" className="btn-close" onClick={() => setIsModalOpen(false)} aria-label="Close"></button>
//                         </div>
//                         <div className="modal-body">
//                           <p>Your investment deposit request for <span style={{ color: "#212529" }}>${parseFloat(amount).toFixed(2)}</span> via <span style={{ color: "#212529" }}>{selectedMethod}</span> for the <span style={{ color: "#212529" }}>{selectedPlan.name}</span> plan has been submitted.</p>
//                           <p>Please send the amount to the following wallet address:</p>
//                           <div className="qr-code-container">
//                             <QRCodeCanvas value={walletAddresses[selectedMethod] || ""} size={128} />
//                             <p className="wallet-address">{walletAddresses[selectedMethod] || "Not available"}</p>
//                             <button type="button" className="btn btn-outline-secondary" onClick={copyToClipboard}>Copy Address</button>
//                           </div>
//                         </div>
//                         <div className="modal-footer">
//                           <button type="button" className="btn btn-primary" onClick={() => setIsModalOpen(false)}>Close</button>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="modal-backdrop fade show" onClick={() => setIsModalOpen(false)}></div>
//                 </>
//               )}
//             </div>

//             {/* Footer */}
// <Footer/>
//             <div style={{ position: "fixed", left: 0, bottom: 0, zIndex: 1, width: "100%" }}>
//               <div style={{ width: "100%", backgroundColor: "#FFFFFF", overflow: "hidden", boxSizing: "content-box", textAlign: "right", lineHeight: "14px", fontSize: "12px", fontFeatureSettings: "normal", textSizeAdjust: "100%", padding: "0px", margin: "0px" }}>
//                 <div style={{ height: "40px" }}>
//                   <iframe src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover=" width="100%" height={36} scrolling="auto" marginWidth={0} marginHeight={0} frameBorder={0} border={0} style={{ border: 0, margin: 0, padding: 0 }}></iframe>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default InvestmentFlow;

